import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  pickupStop,
  deliverStop,
  clearSelectedStop,
  addDriverPhoto,
  updateDriverStop,
} from '../store/routeSlice';
import { openLightbox, showToast } from '../store/uiSlice';
import { getEventCoords } from '../services/lastKnownPosition'; // CLAUDE-SYNC: v1.7.20
import { StatusBadge, ActivityTimeline, resolveSrc } from '../components/index';
import CameraModal from '../components/CameraModal';
import ExceptionPanel from './ExceptionPanel';
import { STATUS_STRING_TO_NUMERIC, STATUS_API_VALUES, getStatusClass, getStatusLabel } from '../constants/status';
import { updateRouteStopStatus, uploadDriverStopPhoto } from '../services/routeService';
import { utcTimeToLocal } from '../Utils';
import { updateDriverNotes } from '../services/stopsService';
import ErrorBoundaryDetail from '../components/ErrorBoundaryDetail';

// ── Helpers ───────────────────────────────────────────────────────────────────

function totalSamples(specimenCounts = {}) {
  return Object.values(specimenCounts).reduce((sum, n) => sum + Number(n), 0);
}

// Inline lightbox for driver photos
function Lightbox({ url, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 16, right: 16,
          background: 'rgba(255,255,255,0.15)', border: 'none',
          color: '#fff', fontSize: 22, width: 40, height: 40,
          borderRadius: '50%', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >✕</button>
      <img
        src={url}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '100%', maxHeight: '90vh',
          objectFit: 'contain', borderRadius: 8,
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
        }}
        alt="Preview"
      />
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
function DriverDetailInner() {
  const dispatch = useDispatch();

  // State
  const [excOpen,        setExcOpen]        = useState(false);
  const [cameraOpen,     setCameraOpen]     = useState(false);
  const [cameraType,     setCameraType]     = useState('pickup');  // 'pickup' | 'delivery'
  const [cameraTitle,    setCameraTitle]    = useState('Add');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [advancing,      setAdvancing]      = useState(false);
  const [lightboxUrl,    setLightboxUrl]    = useState(null);

  // ── Redux ──────────────────────────────────────────────────────────────────
  const { stops, selectedStopId } = useSelector((state) => state.route);

  // API shape: stops = [{ id, assignedName, stops: [{...stopData}] }]
  const allStops = stops?.[0]?.stops ?? [];
  const stop = allStops.find((x) => x.id === selectedStopId);

  if (!stop) return null;

  // ── Derived values (all using real API field names) ────────────────────────
  const statusNumeric = STATUS_STRING_TO_NUMERIC[stop.status] ?? 0;
  const samples = totalSamples(stop.collectedCounts);
  const photos = stop.photos ?? [];

  // Photos split by type
  const pickupPhotos   = photos.filter((p) => p.photoType === 'pickup');
  const deliveryPhotos = photos.filter((p) => p.photoType === 'delivery');
  const phlebPhotos    = photos.filter((p) => !['pickup','delivery'].includes(p.photoType));

  // Whether driver has taken the required photo for the current action
  const hasPickupPhoto   = pickupPhotos.length > 0;
  const hasDeliveryPhoto = deliveryPhotos.length > 0;

  // ── Camera handlers ────────────────────────────────────────────────────────
  const openCamera = (type, title) => {
    setCameraType(type);
    setCameraTitle(title);
    setCameraOpen(true);
  };

  const handleCameraCapture = async (file, previewDataUrl) => {
    setCameraOpen(false);
    setUploadingPhoto(true);
    try {
      const res = await uploadDriverStopPhoto(stop.id, cameraType, file);
      const up  = res?.data ?? {};
      const newPhoto = {
        id:        up.photoId   ?? `local_${Date.now()}`,
        url:       up.url       ?? null,
        fileKey:   up.fileKey   ?? null,
        photoType: up.photoType ?? cameraType,
        label:     cameraType === 'pickup' ? 'Pickup Photo' : 'Delivery Photo',
        timestamp: up.timestamp ?? new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        previewUrl: previewDataUrl,
      };
      dispatch(addDriverPhoto({ stopId: stop.id, photo: newPhoto }));
      dispatch(showToast(`${newPhoto.label} saved ✓`));
    } catch (err) {
      console.error('Photo upload failed', err);
      dispatch(showToast('Photo upload failed — try again'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  // CLAUDE-SYNC: v1.7.12 (2026-09-02) — capture optional GPS coordinates on
  // Arrived/Delivered, mirroring the Clock In/Out location pattern in
  // TopBar.jsx. Best-effort: never blocks the status update if geolocation
  // is denied, unsupported, or times out. Foundation for the future
  // mileage-audit feature (per Tanaya).
  // CLAUDE-SYNC: v1.7.20 — now uses the shared last-known-position helper
  // (instant when the background watch has a recent fix; falls back to the
  // old one-shot lookup). See services/lastKnownPosition.js.
  const getCoordsSafe = () => getEventCoords();

  // ── Status advance ─────────────────────────────────────────────────────────
    const handleAdvance = async (nextNumeric, apiStatus, reduxAction) => {
    if (advancing) return;
    setAdvancing(true);
    try {
      // CLAUDE-SYNC: v1.7.20 — capture coordinates on EVERY status change
      // (arrived, in progress, in transit / "taking it with me", ready /
      // "leaving it here", picked up, delivered), not just arrived/delivered,
      // so the Clock-In Audit export can map the full route (per Tanaya).
      const coords = await getCoordsSafe();
      const result = await updateRouteStopStatus(stop.id, apiStatus, coords?.lat, coords?.lng);
      const rawTs = result?.data?.timestamp || result?.data?.updatedAt;
      const confirmedDate = rawTs ? new Date(rawTs) : new Date();

      // ── Store UTC HH:MM so ActivityTimeline's utcTimeToLocal() converts it correctly
      const hh = String(confirmedDate.getUTCHours()).padStart(2, '0');   // ← getUTCHours not getHours
      const mm = String(confirmedDate.getUTCMinutes()).padStart(2, '0'); // ← getUTCMinutes not getMinutes
      const timeHHMM = `${hh}:${mm}`;

      dispatch(reduxAction(stop.id));
      dispatch(updateDriverStop({
        stopId: stop.id,
        updates: {
          timeline: [
            ...(stop.timeline ?? []),
            { status: apiStatus, time: timeHHMM, role: 'driver', date: result?.data?.date },
          ],
        },
      }));
      dispatch(showToast(`${getStatusLabel(nextNumeric)} ✓`));
    } catch (err) {
      console.error('Status update failed', err);
      dispatch(showToast('Failed to update status — try again'));
    } finally {
      setAdvancing(false);
    }
  };

  const handlePickup = () =>
    handleAdvance(5, STATUS_API_VALUES[5], pickupStop);

  const handleDeliver = () =>
    handleAdvance(6, STATUS_API_VALUES[6], deliverStop);

  // ── View phleb photos in global lightbox ──────────────────────────────────
  const handleViewPhlebPhotos = () => {
    dispatch(openLightbox({
      photos:   phlebPhotos,
      title:    stop.facilityName,
      subtitle: `Photos from phlebotomist · Window ${utcTimeToLocal(stop.timeWindow)}`,
    }));
  };

  // CLAUDE-SYNC: v1.4 S7 — multiple pickup/delivery photos allowed (2026-06-23)
  // Previously only one photo was allowed per action (camera hidden after first photo)
  // Now: + button always visible, any number of photos allowed
  const renderPhotoStrip = (label, photoType, photoList, isUploading) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--bodygray)', marginBottom: 8 }}>{label}</div>
      <div className="photo-strip">
        {/* Previously taken photos */}
        {photoList.map((p) => {
          const src = resolveSrc(p);
          return (
            <div
              key={p.id}
              className="photo-thumb taken"
              style={{ position: 'relative', overflow: 'hidden', cursor: src ? 'pointer' : 'default' }}
              onClick={() => src && setLightboxUrl(src)}
            >
              {src ? (
                <>
                  <img src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={p.label} />
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 14 }}>🔍</span>
                  </div>
                </>
              ) : (
                <><div style={{ fontSize: 16 }}>✅</div><div>Saved</div></>
              )}
            </div>
          );
        })}
        {/* + button — always visible for multiple photos */}
        <div
          className={`photo-thumb ${isUploading ? 'taken' : ''}`}
          style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}
          onClick={() => !isUploading && openCamera(photoType, label)}
        >
          {isUploading ? (
            <><div style={{ fontSize: 14 }}>⏳</div><div>Saving…</div></>
          ) : (
            <><div style={{ fontSize: 20, color: 'var(--berry)' }}>📷</div><div>{photoList.length > 0 ? 'Add' : 'Add'}</div></>
          )}
        </div>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}

      <ExceptionPanel
        stopId={stop.id}
        facilityId={stop.facilityId}
        stopName={stop.facilityName}
        open={excOpen}
        onClose={() => setExcOpen(false)}
      />

      <CameraModal
        isOpen={cameraOpen}
        title={cameraTitle}
        onCapture={handleCameraCapture}
        onClose={() => setCameraOpen(false)}
      />

      <div className="detail-panel open">
        {/* ── Header ── */}
        <div className="detail-header">
          <button className="back-btn" onClick={() => dispatch(clearSelectedStop())}>← Back</button>
          <div>
            <div className="detail-title">{stop.facilityName}</div>
            <div className="detail-subtitle">{stop.addr} · Window {utcTimeToLocal(stop.timeWindow)}</div>
          </div>
        </div>

        <div className="detail-body">
          {/* ── Current Status ── */}
          <div className="card">
            <div className="card-label">Current Status</div>
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <span
                className={`stop-badge ${getStatusClass(statusNumeric)}`}
                style={{ fontSize: 14, padding: '6px 18px' }}
              >
                {getStatusLabel(statusNumeric)}
              </span>
              {stop.dropFacilityName && (
                <div style={{ color: '#b36a00', fontWeight: 600, marginTop: "1rem", fontSize: "10px" }}>
                  📦 Pickup at: {stop.dropFacilityName}
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--bodygray)', textAlign: 'center' }}>
              {samples} samples · Window {utcTimeToLocal(stop.timeWindow)}
            </div>
          </div>

          {/* ── From the Facility ── */}
          <div className="card">
            <div className="card-label">From the Facility</div>

            {/* Phleb photos with notes */}
            {phlebPhotos.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {/* Specimen photos that have notes */}
                {phlebPhotos
                  .filter(p => p.notes)
                  .map((p, i) => {
                    const src = resolveSrc(p);
                    return (
                      <div key={p.fileKey || i} style={{
                        display: 'flex', gap: 10, alignItems: 'flex-start',
                        marginBottom: 10, padding: '8px 10px',
                        background: '#F7F4F8', borderRadius: 10,
                      }}>
                        {src && (
                          <img
                            src={src}
                            onClick={() => setLightboxUrl(src)}
                            style={{
                              width: 52, height: 52, objectFit: 'cover',
                              borderRadius: 8, flexShrink: 0, cursor: 'pointer',
                            }}
                            alt={`Specimen ${i + 1}`}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--rp)', marginBottom: 3 }}>
                            Specimen {i + 1} — Location note
                          </div>
                          <div style={{ fontSize: 12, color: '#333', lineHeight: 1.5 }}>
                            {p.notes}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Phlebotomist note */}
            {stop.notes && (
              <div className="note-preview" style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 14 }}>✎</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#b36a00' }}>Phlebotomist note</div>
                  {stop.notes}
                </div>
              </div>
            )}

            {/* Driver notes from phleb */}
            {stop.phlebNotes && (
              <div className="note-preview" style={{ marginBottom: 12, borderLeftColor: '#1565C0' }}>
                <span style={{ fontSize: 14 }}>📋</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#1565C0' }}>Phleb note for courier</div>
                  {stop.phlebNotes}
                </div>
              </div>
            )}

            {/* Phleb photos */}
            {phlebPhotos.length > 0 ? (
              // CLAUDE-SYNC: v1.4 S8 — renamed to View All Photos, includes courier photos too (2026-06-23)
              <button className="view-photos-btn" onClick={handleViewPhlebPhotos}>
                <div style={{ fontSize: 22, color: 'var(--berry)' }}>📷</div>
                <div className="view-photos-btn-text">
                  <div className="view-photos-btn-title">View All Photos</div>
                  <div className="view-photos-btn-sub">
                    {phlebPhotos.length} from phlebotomist
                    {(pickupPhotos.length + deliveryPhotos.length) > 0
                      ? ` · ${pickupPhotos.length + deliveryPhotos.length} from courier`
                      : ''}
                  </div>
                </div>
                <div className="view-photos-badge">{photos.length}</div>
              </button>
            ) : (
              !stop.notes && !stop.driverNotes && (
                <div style={{ fontSize: 13, color: 'var(--bodygray)' }}>No photos or notes yet.</div>
              )
            )}
          </div>

          {/* ── Driver Actions ── */}
          <div className="card">
            <div className="card-label">Courier Actions</div>

            {/* ── READY: take pickup photo + confirm pickup ── */}
            {statusNumeric === 3 && (
              <>
                {renderPhotoStrip(
                  'Pickup Photo',
                  'pickup',
                  pickupPhotos,
                  uploadingPhoto && cameraType === 'pickup',
                )}
                {!hasPickupPhoto && (
                  <div style={{ fontSize: 12, color: '#b36a00', marginBottom: 8, textAlign: 'center' }}>
                    📷 Take a photo before confirming pickup
                  </div>
                )}
                <button
                  className="action-btn primary"
                  onClick={handlePickup}
                  disabled={advancing || !hasPickupPhoto}
                  style={{ opacity: (advancing || !hasPickupPhoto) ? 0.5 : 1 }}
                >
                  {advancing ? 'Updating…' : 'Confirm Pickup'}
                </button>
              </>
            )}

            {statusNumeric === 4 && (
              <div style={{ fontSize: 13, color: 'var(--bodygray)', textAlign: 'center', padding: '12px 0' }}>
                🚐 In transit — phlebotomist is finalizing the destination
              </div>
            )}

            {/* ── PICKED UP: take delivery photo + confirm delivery ── */}
            {statusNumeric === 5 && (
              <>
                {renderPhotoStrip(
                  'Delivery Photo',
                  'delivery',
                  deliveryPhotos,
                  uploadingPhoto && cameraType === 'delivery',
                )}
                {!hasDeliveryPhoto && (
                  <div style={{ fontSize: 12, color: '#b36a00', marginBottom: 8, textAlign: 'center' }}>
                    📷 Take a photo before confirming delivery
                  </div>
                )}
                <button
                  className="action-btn primary"
                  onClick={handleDeliver}
                  disabled={advancing || !hasDeliveryPhoto}
                  style={{ opacity: (advancing || !hasDeliveryPhoto) ? 0.5 : 1 }}
                >
                  {advancing ? 'Updating…' : 'Confirm Delivery to Lab'}
                </button>
              </>
            )}

            {/* ── DELIVERED ── */}
            {statusNumeric >= 6 && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 32 }}>✅</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--rp)', marginTop: 6 }}>
                  Delivered to Lab 
                </div>
              </div>
            )}

            {/* ── NOT YET READY ── */}
            {statusNumeric < 3 && (
              <div style={{ fontSize: 13, color: 'var(--bodygray)', textAlign: 'center', padding: '12px 0' }}>
                Waiting for phlebotomist — not yet ready for pickup
              </div>
            )}

            <button
              className="action-btn danger"
              style={{ marginTop: 8 }}
              onClick={() => setExcOpen(true)}
            >
              Report Exception
            </button>
          </div>

          {/* ── Facility Info ── */}
          <div className="card">
            <div className="card-label">Facility Info</div>
            <div style={{ fontSize: 13, color: 'var(--bodygray)', lineHeight: 2 }}>
              {stop.phone   && <div>📞 {stop.phone}</div>}
              {stop.contact && <div>👤 {stop.contact}</div>}
              {stop.phlebName && <div>🩸 Phleb: {stop.phlebName}</div>}
            </div>
          </div>

          {/* ── Courier Note ── */}
          {/* CLAUDE-SYNC: v1.4 S9 — courier can flag errors without editing timeline (2026-06-23) */}
          {/* e.g. "accidentally hit Delivered — actual time was 9:40" */}
          <div className="card">
            <div className="card-label">Courier Note</div>
            <div style={{ fontSize: 11, color: 'var(--bodygray)', marginBottom: 8 }}>
              Flag a mistake or add context without changing the timeline.
            </div>
            {stop.driverNotes && (
              <div style={{ background: '#FEF3E2', borderRadius: 10, padding: '10px 12px', marginBottom: 10, fontSize: 12, color: '#b36a00' }}>
                <strong>Your note:</strong> {stop.driverNotes}
              </div>
            )}
            <CourierNoteField stopId={stop.id} existingNote={stop.driverNotes || ''} />
          </div>

          {/* ── Activity Timeline ── */}
          <div className="card">
            <div className="card-label">Activity Timeline</div>
            <ActivityTimeline
              timelines={stop.timeline ?? []}
              phlebName={stop.phlebName}
              driverName={stop.driverName}
              isDriver={true}
            />
          </div>

        </div>
      </div>
    </>
  );
}

// CLAUDE-SYNC: v1.4 S9 — CourierNoteField: free-text note, does NOT edit timeline (2026-06-23)
// BACKEND: PUT /api/stops/:id/courier-note { courierNote: string }
function CourierNoteField({ stopId, existingNote }) {
  const dispatch = useDispatch();
  const [note, setNote] = useState(existingNote);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await updateDriverNotes(stopId, note);
      dispatch(showToast('Note saved ✓'));
    } catch {
      dispatch(showToast('Failed to save note'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <textarea
        rows={3}
        placeholder="e.g. Accidentally hit Delivered — actual delivery time was 9:40"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{
          width: '100%', padding: '10px 12px', border: '1.5px solid var(--rgray)',
          borderRadius: 10, fontFamily: 'inherit', fontSize: 12, color: '#333',
          resize: 'none', outline: 'none',
        }}
      />
      <button
        onClick={handleSave}
        disabled={saving || !note.trim()}
        style={{
          marginTop: 8, width: '100%', padding: 10, borderRadius: 10, border: 'none',
          background: note.trim() ? 'var(--berry)' : '#ccc',
          color: 'white', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          cursor: note.trim() ? 'pointer' : 'not-allowed',
        }}
      >
        {saving ? 'Saving…' : 'Save Note'}
      </button>
    </div>
  );
}

function DriverDetail() {
  const dispatch = useDispatch();
  const handleBack = () => dispatch(clearSelectedStop());
  return (
    <ErrorBoundaryDetail onBack={handleBack}>
      <DriverDetailInner />
    </ErrorBoundaryDetail>
  );
}

export default DriverDetail;